import React, { useState, useEffect, useRef } from "react";
import { X, Loader, ImagePlus, FileText } from "lucide-react";
import SearchableSelect from "./SearchableSelect";
import { formatNumberWithSeparator } from "../utils/numberFormat";
import * as inventoryService from "../api/inventoryService";
import { accountService } from "../api/accountService";
import { useNotification } from "../contexts/NotificationContext";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_MIME = ["image/png", "image/jpeg", "image/jpg", "application/pdf"];

export default function StockAdjustmentModal({ isOpen, onClose, onSave }) {
  const { showNotification } = useNotification();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [saving, setSaving] = useState(false);

  const [adjustmentType, setAdjustmentType] = useState("Stok Keluar");
  const [amount, setAmount] = useState(0);
  const [notes, setNotes] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const initialStock = selectedProduct?.stock || 0;
  const unitLabel = selectedProduct ? (selectedProduct.unit || "PCS").toString().toUpperCase() : "-";
  const finalStock =
    adjustmentType === "Stok Masuk"
      ? initialStock + Number(amount || 0)
      : initialStock - Number(amount || 0);

  const resetForm = () => {
    setSelectedProduct(null);
    setAdjustmentType("Stok Keluar");
    setAmount(0);
    setNotes("");
    setAttachment(null);
  };

  const handleClose = () => {
    if (saving) return;
    resetForm();
    onClose();
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!ALLOWED_MIME.includes(file.type)) {
      showNotification("error", "Format file tidak didukung (PNG, JPG, PDF)");
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      showNotification("error", "Ukuran file melebihi 10MB");
      return;
    }
    setAttachment(file);
  };

  const handleFileInputChange = (e) => {
    const file = e.target.files?.[0];
    handleFileSelect(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  const handleSave = async () => {
    if (!selectedProduct) {
      showNotification("error", "Pilih produk terlebih dahulu");
      return;
    }
    if (!amount || amount <= 0) {
      showNotification("error", "Jumlah penyesuaian harus lebih dari 0");
      return;
    }
    if (!notes.trim()) {
      showNotification("error", "Keterangan wajib diisi");
      return;
    }

    try {
      setSaving(true);
      
      let uploadedUrl = "";
      if (attachment) {
        try {
          const uploadRes = await accountService.uploadProof(attachment, "adjustments");
          const uploadData = uploadRes?.data || uploadRes || {};
          uploadedUrl = uploadData.url || uploadData.file_url || uploadData.path || "";
          if (!uploadedUrl) {
            showNotification("error", "Gagal mengunggah file lampiran");
            setSaving(false);
            return;
          }
        } catch (uploadErr) {
          console.error("Attachment upload error:", uploadErr);
          showNotification("error", "Gagal mengunggah file lampiran");
          setSaving(false);
          return;
        }
      }

      const today = new Date();
      const date = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const payload = {
        date,
        notes: notes.trim(),
        attachment_url: uploadedUrl,
        items: [
          {
            product_id: selectedProduct.product_id || selectedProduct.id,
            variant_id: selectedProduct.variant_id || null,
            option_id: selectedProduct.option_id || null,
            type: adjustmentType === "Stok Masuk" ? "IN" : "OUT",
            qty: Number(amount),
            reason: notes.trim(),
          },
        ],
      };
      await inventoryService.createStockAdjustment(payload);
      showNotification("success", "Penyesuaian stok berhasil disimpan");
      resetForm();
      onClose();
      if (onSave) onSave();
    } catch (err) {
      console.error("Failed to save adjustment:", err);
      showNotification(
        "error",
        err.response?.data?.message || err.message || "Gagal menyimpan penyesuaian stok",
      );
    } finally {
      setSaving(false);
    }
  };

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const fetchProductOptions = async (term) => {
    try {
      const isApps = localStorage.getItem("PREVIEW_APPS") === "true";
      const response = await inventoryService.getStocks({
        limit: 50,
        query: term,
      });
      const rows = response?.data || [];
      return rows
        .filter((row) => !isApps || (!row.brand_name && !row.brand_id))
        .map((row) => ({
          id: row.id,
          product_id: row.product_id,
          variant_id: row.variant_id || null,
          name: (() => {
            const codePart = row.product_code && row.product_code !== "-" ? `[${row.product_code}] ` : "";
            const variantPart = row.variant_name ? ` (${row.variant_name})` : " (DEFAULT)";
            return `${codePart}${row.product_name}${variantPart} - ${row.unit_name || "PCS"}`;
          })(),
          stock:
            row.current_stock ?? row.current_qty ?? row.quantity ?? 0,
          unit: row.unit_name || "PCS",
        }));
    } catch (err) {
      console.error("Failed to fetch products:", err);
      return [];
    }
  };

  return (
    <div
      onClick={handleClose}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4 modal-backdrop-enter">
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl ring-1 ring-slate-200/70 w-full max-w-xl flex flex-col max-h-[92vh] overflow-y-auto shadow-2xl modal-panel-enter">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 bg-teal-600 text-white sticky top-0 z-10">
          <h2 className="text-base font-semibold tracking-tight">
            Tambah Penyesuaian Stok
          </h2>
          <button
            onClick={handleClose}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-60">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-5">
          {/* Pilih Produk */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Pilih Produk <span className="text-rose-500">*</span>
            </label>
            <SearchableSelect
              options={selectedProduct ? [selectedProduct] : []}
              value={selectedProduct?.id || ""}
              onChange={(_id, opt) => setSelectedProduct(opt || null)}
              fetchOptions={fetchProductOptions}
              minSearchLength={0}
              placeholder="Pilih salah satu data produk..."
              displayKey="name"
              valueKey="id"
            />
          </div>

          {/* Penyesuaian + Jumlah */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Penyesuaian <span className="text-rose-500">*</span>
              </label>
              <SearchableSelect
                options={[
                  { value: "Stok Masuk", label: "Stok Masuk (+)" },
                  { value: "Stok Keluar", label: "Stok Keluar (-)" },
                ]}
                value={adjustmentType}
                onChange={(value) => setAdjustmentType(value)}
                placeholder="Contoh: Stok Keluar"
                displayKey="label"
                valueKey="value"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Jumlah <span className="text-rose-500">*</span>
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={amount ? formatNumberWithSeparator(amount) : ""}
                  onChange={(e) =>
                    setAmount(
                      parseFloat(e.target.value.replace(/\./g, "")) || 0,
                    )
                  }
                  className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-3 pr-12 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors"
                  placeholder="Contoh: 1"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 text-xs font-medium">
                  {unitLabel}
                </span>
              </div>
            </div>
          </div>

          {/* Stok Awal + Stok Akhir */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Stok Awal
              </label>
              <div className="bg-teal-50 text-teal-700 px-3 py-2 rounded-lg ring-1 ring-teal-200 flex justify-between items-center">
                <span className="font-bold">
                  {selectedProduct
                    ? formatNumberWithSeparator(initialStock)
                    : "-"}
                </span>
                <span className="text-xs font-medium">{unitLabel}</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1.5">
                Stok Akhir
              </label>
              <div className="bg-orange-50 text-orange-700 px-3 py-2 rounded-lg ring-1 ring-orange-200 flex justify-between items-center">
                <span className="font-bold">
                  {selectedProduct
                    ? formatNumberWithSeparator(finalStock)
                    : "-"}
                </span>
                <span className="text-xs font-medium">{unitLabel}</span>
              </div>
            </div>
          </div>

          {/* Keterangan */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Keterangan <span className="text-rose-500">*</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent focus:bg-white transition-colors h-24 resize-none"
              placeholder="Masukan keterangan penyesuaian stok disini..."
            />
          </div>

          {/* Lampiran */}
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">
              Lampiran
            </label>
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-xl px-4 py-6 text-center cursor-pointer transition-colors ${
                isDragging
                  ? "border-teal-500 bg-teal-50/60"
                  : "border-slate-300 bg-slate-50/40 hover:border-teal-400 hover:bg-teal-50/30"
              }`}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".png,.jpg,.jpeg,.pdf"
                className="hidden"
                onChange={handleFileInputChange}
              />
              {attachment ? (
                <div className="flex items-center justify-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="text-left min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {attachment.name}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      {(attachment.size / 1024).toFixed(1)} KB · Klik untuk
                      ganti
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setAttachment(null);
                    }}
                    className="ml-2 text-rose-500 hover:text-rose-700 text-xs font-semibold">
                    Hapus
                  </button>
                </div>
              ) : (
                <>
                  <div className="w-10 h-10 mx-auto rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center mb-2">
                    <ImagePlus className="w-5 h-5" />
                  </div>
                  <p className="text-sm">
                    <span className="text-teal-600 font-semibold">
                      Upload a file
                    </span>{" "}
                    <span className="text-slate-500">or drag and drop</span>
                  </p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    PNG, JPG, PDF up to 10MB
                  </p>
                </>
              )}
            </div>
            {attachment && (
              <p className="text-[11px] text-emerald-600 mt-1.5">
                File siap diunggah ke server.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/40 flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={saving}
            className="px-5 py-2 bg-white ring-1 ring-slate-200 text-slate-700 rounded-lg font-medium text-sm hover:bg-slate-50 transition-colors disabled:opacity-50">
            Batal
          </button>
          <button
            onClick={handleSave}
            className={`px-5 py-2 text-white rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
              selectedProduct && !saving
                ? "bg-teal-600 hover:bg-teal-700 shadow-sm shadow-teal-600/20"
                : "bg-slate-200 text-slate-400 cursor-not-allowed"
            }`}
            disabled={!selectedProduct || saving}>
            {saving && <Loader className="w-4 h-4 animate-spin" />}
            {saving ? "Memproses..." : "Proses Penyesuaian"}
          </button>
        </div>
      </div>
    </div>
  );
}
